/**
 * Cross-mount, cross-reload memory for the discovery feed, scoped to one
 * browser tab's session.
 *
 * DiscoveryFeed unmounts every time the shopper leaves /discover for the
 * wardrobe, profile, or any other route - react-router tears down the whole
 * previous route's component tree - and remounts fresh when they come back.
 * A plain refresh of the page does the same thing to every bit of JS state,
 * component or module scope alike: the whole script re-runs from nothing.
 * Two things depended on surviving both:
 *
 *   - The deck replayed from card one, including cards this same session had
 *     already swiped, because the "what have I shown this person" Set reset
 *     to empty.
 *   - MAHI's "I'm running low on X, want me to mix in some Y?" line fired
 *     again on every return trip or refresh, because nothing remembered it
 *     had already been said - the backend recomputes the same honest answer
 *     from the same still-thin pool every time it's asked.
 *
 * sessionStorage is what actually spans that: unlike a module-scope variable
 * (which only survives while the script keeps running, i.e. SPA navigation
 * but not a reload), it survives a hard refresh too, and unlike localStorage
 * it still clears itself when the tab closes - "this browsing session" in
 * the literal, browser-defined sense, not "forever on this device". Reads
 * and writes are wrapped in try/catch: a device with storage blocked (private
 * browsing, some in-app webviews) just gets no cross-reload memory rather
 * than a broken feed.
 */

const SEEN_SKUS_KEY = 'mahi_session_seen_skus';
const SHOWN_NOTICES_KEY = 'mahi_session_shown_notices';

function loadSet(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set(); // storage blocked - falls back to in-memory only for this reload
  }
}

function persistSet(key, set) {
  try {
    sessionStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch { /* storage blocked - the in-memory Set still works until the next reload */ }
}

// Every sku this browser session has had a verdict on (save, pass, or
// try-on), across every deck the session has shown - the default feed and
// any MAHI search alike. A Set subclass rather than a plain Set so every
// existing `seenSkusRef.current.add(sku)` / `.delete(sku)` call site (there
// to drive Undo) keeps working unchanged while also persisting - the whole
// point is that callers don't have to know this survives a reload.
class PersistedSkuSet extends Set {
  add(sku) {
    super.add(sku);
    persistSet(SEEN_SKUS_KEY, this);
    return this;
  }

  delete(sku) {
    const removed = super.delete(sku);
    persistSet(SEEN_SKUS_KEY, this);
    return removed;
  }
}

export const sessionSeenSkus = new PersistedSkuSet(loadSet(SEEN_SKUS_KEY));

// Notice text already surfaced this session. Text is the key rather than a
// boolean flag because a *different* notice - a new category mix, a deeper
// widening rung - is new information and still deserves to be said once.
const shownNotices = loadSet(SHOWN_NOTICES_KEY);

export function shouldShowNotice(text) {
  return Boolean(text) && !shownNotices.has(text);
}

export function markNoticeShown(text) {
  if (!text) return;
  shownNotices.add(text);
  persistSet(SHOWN_NOTICES_KEY, shownNotices);
}
